
import {
  RPCPublishmentName,
  IRPCPublishment,
  IInjectTransport,
  
  ISerializedRequest,
  ISerializedError,
  ISerializedResponse,

  RequestIdentifier,
  IRequestMetadata,

  RequestUpdatedEventName,
} from '@staffbar/types'

import {
  IEnhancementRecord
} from '@staffbar/types';


/**
 * These serializations are aimed at helping us communicate fetch request across frames.
 * 
 * 
 */
const RequestKeys = [
  'cache',
  'credentials',
  'destination',
  // 'headers', // Special case
  'integrity',
  'isHistoryNavigation', //  boolean
  'isReloadNavigation', // boolean
  'keepalive',
  'method',
  'mode',
  'redirect',
  'referrer',
  'referrerPolicy',
  // 'signal', // function, which we'll ignore
  'url',
  // 'body', // Special case
]

export const serializeHeaders = (headers: Headers): { [key: string]: string} => {
  let outputHeaders: { [key: string]: string } = {}
  headers.forEach((value, key) => {
  outputHeaders[key] = value
  })
  return outputHeaders
}

export const serializeRequest = ({
  request,
  requestBody,
  enhancement,
  initiatedTimestamp,
 } :{ 
   request: Request, 
   requestBody?: string
   initiatedTimestamp: number
   enhancement?: IEnhancementRecord, 
  }): ISerializedRequest => {
  let headers = serializeHeaders(request.headers)
  return { 
    enhancement,
    headers,
    body: requestBody,
    initiatedTimestamp,
    ...Object.assign({}, ...RequestKeys.map((key) => {
      let value = (request as any)[key]
      if (!value) { return undefined }
      return { [key]: value }
    }).filter(x => x !== undefined))
  }
}

export const deserializeRequest = (requestObject: ISerializedRequest): Request => {
  let { url, body, ...rest} = requestObject
  let toSendBody = body && body.length > 0 ? body : undefined;
  return new Request(url, { body: toSendBody, ...rest}) 
}


const ResponseKeys = [
  // 'headers', // Special case
  'ok',
  'redirected',
  'status',
  'statusText',
  'type',
  'url',
]

export const serializeResponse = (response: Response, responseText?: string, completedTimestamp?: number): ISerializedResponse => {
  return {
    completedTimestamp,
    headers: serializeHeaders(response.headers),
    body: responseText,
    ...Object.assign({}, ...ResponseKeys.map((key) => {
        let value = (response as any)[key]
        if (!value) { return undefined }
        return { [key]: value }
    }).filter(x => x !== undefined))
  }
}

export const deserializeResponse = (responseObject: { [key: string]: any}): Response => {
  let responseValues = responseObject.response
  let response = new Response('', responseValues)
  response.text()
  return response
}


export const serializeError = (error: Error): ISerializedError  => {
  return {
    errorType: error.name,
    message: error.message,
  }
}


export interface ISimpleFetchPublisher {
  onRequestStarted({
    id, 
    request, 
    initiatedTimestamp, 
    enhancement 
  }: {
    id: RequestIdentifier, 
    request: Request, 
    requestBody?: string, 
    initiatedTimestamp: number,
    enhancement?: IEnhancementRecord 
  }): void;
  onRequestUpdated(parameters: { [key: string]: any }, eventName: RequestUpdatedEventName): void;
}

export const serializeNativeRequest = (parameters: { [key: string]: any }): IRequestMetadata => {
  const {
    request: nativeRequest,
    response: nativeResponse,
    requestBody,
    responseText: nativeResponseText,
    error: nativeError,
    initiatedTimestamp,
    completedTimestamp,
    ...rest
  } = parameters

  var message = { ...rest }

  if (nativeRequest && initiatedTimestamp) {
    // TODO: If I put a debugger here, I can create the race that makes reuqests fail
    message['request'] = serializeRequest({ 
      request: nativeRequest, 
      requestBody,
      initiatedTimestamp,
      enhancement: parameters.enhancement 
    })
  } 

  if (nativeResponseText && nativeResponse) {
    message['response'] = serializeResponse(nativeResponse, nativeResponseText, completedTimestamp)
  }

  if (nativeError) {
    message['error'] = serializeError(nativeError);
  }
  return message as IRequestMetadata
}

export class SimpleFetchPublisher implements ISimpleFetchPublisher {

  constructor({ injectTransport }: {  injectTransport: IInjectTransport  }) {
    this._injectTransport = injectTransport 
  }

  private _injectTransport: IInjectTransport;

  onRequestStarted({ 
    id, 
    request, 
    requestBody,
    initiatedTimestamp,
    enhancement
  }: { 
    id: RequestIdentifier, 
    request: Request, 
    requestBody?: string
    initiatedTimestamp: number,
    enhancement?: IEnhancementRecord 
  }) {
    let publishment :IRPCPublishment = {
      publishmentName: RPCPublishmentName.FetchRequestStarted,
      message: {
        id,
        request: serializeRequest({ request, requestBody, initiatedTimestamp, enhancement }),
      }
    }
    this._injectTransport.publish(publishment)
  }

  onRequestUpdated(parameters: { [key: string]: any }, eventName: RequestUpdatedEventName): void {
    const message = serializeNativeRequest(Object.assign({}, parameters, { eventName}))

    let publishemt: IRPCPublishment = {
      publishmentName: RPCPublishmentName.FetchMetadataUpdated,
      message,
    }
    this._injectTransport.publish(publishemt)
  }
}
